import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTheme } from '../hooks/useTheme';
import axios from 'axios';
import './DashboardLogin.css';
import { useAuth } from '../context/AuthContext';

function DashboardLogin() {
	const { isDarkMode } = useTheme();
	const navigate = useNavigate();
	const [email, setEmail] = useState('');
	const [password, setPassword] = useState('');
	const [error, setError] = useState('');
	const [showPassword, setShowPassword] = useState(false);
	const [isLoading, setIsLoading] = useState(false);
	const { login } = useAuth();

	// Fix: Remove the trailing slash and don't add an extra slash in the URL
	const url = process.env.REACT_APP_API_URL || 'http://localhost:8000';

	const signInUser = async (data) => {
		try {
			const response = await axios.post(
				`${url}/api/session/login`,
				data,
				{
					headers: {
						'Content-Type': 'application/json',
					},
					withCredentials: false,
				}
			);

			// Check for token in response body
			let token = response.data.token;

			if (!token) {
				console.warn(
					'No token received in login response. Authentication may fail for protected routes.'
				);

				// Create a temporary token for testing if needed
				// This is just for development/testing and should be removed in production
				token = `Bearer TEMP_${btoa(
					`${response.data.user.id}:${Date.now()}`
				)}`;
				response.data.token = token;
			}

			return {
				data: response.data,
				status: response.status,
			};
		} catch (error) {
			// Your existing error handling
			console.error('Login failed', error);
			if (error.response) {
				console.error('Error status:', error.response.status);
				console.error('Error data:', error.response.data);
				console.error('Error headers:', error.response.headers);
				throw new Error(
					error.response.data.message || 'Invalid credentials'
				);
			} else if (error.request) {
				console.error('No response received:', error.request);
				throw new Error('No response from server');
			} else {
				console.error('Request setup error:', error.message);
				throw new Error('Error setting up request');
			}
		}
	};

	const handleSubmit = async (e) => {
		e.preventDefault();
		setError('');
		setIsLoading(true);

		try {
			const response = await signInUser({ email, password });

			const { user, organizations, token } = response.data;

			// Check if token exists
			if (!token) {
				console.error('No token in login response');
				setError('Authentication error: No token received');
				setIsLoading(false);
				return;
			}

			// Check if user is an admin or has admin access to any organization
			if (
				user.accessLevel === 'Admin' ||
				user.accessLevel === 'SuperAdmin' ||
				(organizations.adminOrgs && organizations.adminOrgs.length > 0)
			) {
				await login(response.data);

				navigate('/dashboard');
			} else {
				setError('You must login with an administrator account');
			}
		} catch (error) {
			console.error('Login submission error:', error);
			setError(error.message || 'Invalid email or password');
		} finally {
			setIsLoading(false);
		}
	};

	return (
		<div className='dashboard-login'>
			<div className='login-container'>
				<img
					src={require(`../assets/${
						isDarkMode ? 'Icon_Gradient' : 'Icon_Primary'
					}.png`)}
					alt='Assemblie Logo'
					className='login-logo'
				/>

				{error && <div className='error-message'>{error}</div>}

				<form
					className='login-form'
					onSubmit={handleSubmit}>
					<input
						type='email'
						placeholder='Email'
						className='login-input'
						value={email}
						onChange={(e) => setEmail(e.target.value)}
					/>
					<div className='password-input-container'>
						<input
							type={showPassword ? 'text' : 'password'}
							placeholder='Password'
							className='login-input'
							value={password}
							onChange={(e) => setPassword(e.target.value)}
						/>
						<button
							type='button'
							className='password-toggle'
							onClick={() => setShowPassword(!showPassword)}>
							<span className='material-icons'>
								{showPassword ? 'visibility_off' : 'visibility'}
							</span>
						</button>
					</div>
					<div className='forgot-password-container'>
						<Link
							to='/forgot-password'
							className='forgot-password'>
							Forgot Password?
						</Link>
					</div>
					<button
						type='submit'
						className='sign-in-btn'
						disabled={isLoading}>
						{isLoading ? <div className='loader'></div> : 'Sign In'}
					</button>
				</form>

				<button
					className='contact-btn'
					onClick={() => navigate('/contact')}>
					Contact Us to Get Started
				</button>
			</div>
		</div>
	);
}

export default DashboardLogin;
